import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';

import PanesWidget from '../PanesWidget/PanesWidget';
import Map from '../Map/Map';
import PibResidenceDetail from '../Residence/PibResidenceDetail';
import FloorPlanDetail from '../FloorPlan/FloorPlanDetail';
import PlaceDetail from '../Place/PlaceDetail';
import SubpaneMediaDetail from '../PanesWidget/SubpaneMediaDetail';
import ResourceDetail from '../MyResources/ResourcesDetail';
import AboutTab from '../AboutTab/AboutTab';
import { useConfigStore } from '../../stores';
import useContentLinkTracking from '../../hooks/useContentLinkTracking';

/**
 * Helper to create resources pane data
 */
const createResourcesPaneData = (resourcesData) => [{
  id: '1',
  title: 'My Resources',
  ...resourcesData,
}];

/**
 * Helper to create the About pane data — one subpane per bucket, plus any extra
 * subpanes (My Resources rides along as a "Resources" tab). PanesWidget renders
 * a subpane tab per entry (labelled by bucket title); a community with a single
 * bucket and no extras hides the buttons and reads as one scrollable page.
 */
export const createAboutPaneData = (buckets, extraSubpanes = []) => [
  ...buckets.map((bucket) => ({
    id: bucket.id,
    title: bucket.title,
    subpaneButtonText: bucket.title,
    sections: bucket.sections || [],
  })),
  ...extraSubpanes,
];

/**
 * Helper to create the Resources subpane appended to the About pane. It carries
 * its own subpaneComponent so Pane renders ResourceDetail for this tab instead
 * of the pane-level AboutTab.
 */
export const createAboutResourcesSubpaneData = (resourcesData, subpaneComponent) => ({
  id: 'resources',
  title: 'Resources',
  subpaneButtonText: 'Resources',
  subpaneComponent,
  ...resourcesData,
});

/**
 * Transform floor plan data into the shape FloorPlanDetail expects.
 *
 * The three inventory fields are gated on platformShowSoldOutStatus, which is
 * what suppresses the sold-out banner and the "N Left" count on a card. On the
 * TourGuide AI Brochure that flag comes from `tgaib_show_sold_out_status`, and
 * for a long time it could never be true: the Drupal field only offered `pib_*`
 * machine names, so the brochure ran permanently with sold-out status off and
 * an admin ticking it saw nothing change.
 *
 * Exported so the gating can be asserted directly — the surrounding component
 * needs stores, a router and an authenticated fetch to render, which is a great
 * deal of scaffolding for a conditional.
 */
export const createFloorPlanPaneData = (floorPlansData, platformShowSoldOutStatus) => (
  floorPlansData.map((floorPlan) => ({
    id: floorPlan.uuid,
    nid: floorPlan.nid,
    title: floorPlan.title,
    subpaneButtonText: floorPlan.subpaneButtonText,
    coverImage: floorPlan.coverImage,
    // twoD and threeD for FloorPlanImagesWidget
    twoD: floorPlan.twoD,
    threeD: floorPlan.threeD,
    bedBathSqft: floorPlan.bedBathSqft,
    description: floorPlan.description,
    photoGalleryUuid: floorPlan.photoGalleryUuid,
    photoGalleryImages: floorPlan.photoGalleryImages,
    videoUuid: floorPlan.videoUuid,
    // FloorPlanDetailCore expects video.videoId and video.service
    video: floorPlan.video ? {
      videoId: floorPlan.video.serviceVideoId,
      service: floorPlan.video.service,
    } : null,
    virtualTourUuid: floorPlan.virtualTourUuid,
    threeSixties: floorPlan.threeSixties,
    // Inventory data for sold out banners (only if feature flag enabled)
    remainingInventory: platformShowSoldOutStatus ? floorPlan.remainingInventory : null,
    soldOut: platformShowSoldOutStatus ? floorPlan.soldOut : null,
    inventoryPhrase: platformShowSoldOutStatus ? floorPlan.inventoryPhrase : null,
  }))
);

// The Amenities & Services blurb (community wysiwyg), shown sticky in the
// amenities pane. A ref-based delegated listener captures link clicks inside
// the blurb without making the static container itself interactive.
const StickyAmenitiesBlurb = ({ html, tracking }) => {
  const ref = useRef(null);
  useContentLinkTracking(ref, tracking);

  return (
    <div className="pibPage-favorites__stickyContent">
      <div className="pibPage-favorites__stickyContent__title">Amenities & Services</div>
      <div
        className="pibPage-favorites__stickyContent__wysiwyg"
        ref={ref}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

StickyAmenitiesBlurb.propTypes = {
  html: PropTypes.string.isRequired,
  tracking: PropTypes.shape({
    endpoint: PropTypes.string,
    token: PropTypes.string,
    identity: PropTypes.shape({
      residentNid: PropTypes.number,
      contactHash: PropTypes.string,
      communityNid: PropTypes.number,
    }),
  }),
};

StickyAmenitiesBlurb.defaultProps = {
  tracking: null,
};

const BrochurePageContent = ({
  communitySlug,
  mapFavoritesData,
  resourcesData,
  amenitiesServicesHTML,
  aboutTitle,
  floorPlansTitle,
  buckets,
  initialFavoriteData,
  favoritesData,
  tracking,
}) => {
  const [panesData, setPanesData] = useState(null);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const { platformShowSoldOutStatus } = featureFlags || {};

  // Use favoritesData from the optimized API endpoint (passed from parent)
  const residencesData = favoritesData?.residences || [];
  const floorPlansData = favoritesData?.floorPlans || [];
  const amenitiesData = favoritesData?.amenities || [];
  const mediaData = favoritesData?.media || [];

  // Memoize the sticky amenities blurb to prevent recreation on every render
  const stickyAmenitiesBlurb = useMemo(() => (
    amenitiesServicesHTML
      ? <StickyAmenitiesBlurb html={amenitiesServicesHTML} tracking={tracking} />
      : null
  ), [amenitiesServicesHTML, tracking]);

  // Memoize components to prevent recreation on every render
  const memoizedMap = useMemo(() => (
    <div className="pibPage-map content-fadeIn">
      <div id="pibPage-mapModalRoot" className="modalRootClass" />
      <Map reset />
    </div>
  ), [mapFavoritesData]);

  // Memoize components to prevent recreation on every render
  const memoizedResidenceDetail = useMemo(() => (
    <PibResidenceDetail
      navClass="pibPage-favorites__detailNav"
      tracking={tracking}
    />
  ), [tracking]);

  const memoizedFloorPlanDetail = useMemo(() => (
    <FloorPlanDetail
      hasInternalHeader
      navClass="pibPage-favorites__detailNav"
      tracking={tracking}
    />
  ), [tracking]);

  const memoizedPlaceDetail = useMemo(() => (
    <PlaceDetail
      hasInternalHeader
      navClass="pibPage-favorites__detailNav"
      tracking={tracking}
    />
  ), [tracking]);

  const memoizedSubpaneMediaDetail = useMemo(() => (
    <SubpaneMediaDetail />
  ), []);

  const memoizedResourceDetail = useMemo(() => (
    <ResourceDetail tracking={tracking} />
  ), [tracking]);

  const memoizedAboutTab = useMemo(() => (
    <AboutTab tracking={tracking} />
  ), [tracking]);

  // Memoize each pane data separately for optimal performance
  const mapPaneData = useMemo(() => ({
    id: 'map',
    btnText: 'Map',
    paneData: [{ id: 'map', title: 'Map' }],
    subpaneComponent: memoizedMap,
    stickyMarkup: null,
  }), [memoizedMap]);

  // Only show resources if there are actual files or links.
  const hasResources = useMemo(() => {
    if (!resourcesData) return false;

    const hasFiles = resourcesData.files && resourcesData.files.length > 0;
    const hasLinks = resourcesData.links && resourcesData.links.length > 0;
    return Boolean(hasFiles || hasLinks);
  }, [resourcesData]);

  const hasAbout = Boolean(buckets && buckets.length > 0);

  const aboutPaneData = useMemo(() => {
    // Show the About pane only if the community has authored buckets. Each
    // bucket becomes a subpane tab; a single bucket collapses to one page.
    if (!hasAbout) return null;

    // With an About pane present, My Resources joins it as a final "Resources"
    // tab rather than standing as its own pane.
    const extraSubpanes = hasResources
      ? [createAboutResourcesSubpaneData(resourcesData, memoizedResourceDetail)]
      : [];

    return {
      id: 'about',
      btnText: aboutTitle,
      paneData: createAboutPaneData(buckets, extraSubpanes),
      subpaneComponent: memoizedAboutTab,
      stickyMarkup: null,
    };
  }, [
    hasAbout,
    buckets,
    aboutTitle,
    memoizedAboutTab,
    hasResources,
    resourcesData,
    memoizedResourceDetail,
  ]);

  const residencesPaneData = useMemo(() => {
    // Show pane only if we have residence data
    if (!residencesData || residencesData.length === 0) return null;

    // Transform data to format expected by PanesWidget
    const paneData = residencesData.map((residence) => ({
      id: residence.uuid,
      nid: residence.nid,
      title: residence.title,
      subpaneButtonText: residence.subpaneButtonText,
      coverImage: residence.coverImage,
      floorPlanUuid: residence.floorPlanUuid,
      floorPlanTitle: residence.floorPlanTitle,
    }));

    return {
      id: 'residences',
      btnText: 'My Residences',
      paneData,
      subpaneComponent: memoizedResidenceDetail,
      stickyMarkup: null,
    };
  }, [residencesData, memoizedResidenceDetail]);

  const floorPlansPaneData = useMemo(() => {
    if (!floorPlansData || floorPlansData.length === 0) return null;

    const paneData = createFloorPlanPaneData(floorPlansData, platformShowSoldOutStatus);

    return {
      id: 'floorPlans',
      btnText: floorPlansTitle,
      paneData,
      subpaneComponent: memoizedFloorPlanDetail,
      stickyMarkup: null,
    };
  }, [floorPlansData, floorPlansTitle, memoizedFloorPlanDetail, platformShowSoldOutStatus]);

  const amenitiesPaneData = useMemo(() => {
    if (!amenitiesData || amenitiesData.length === 0) return null;

    // Transform data to format expected by PanesWidget
    const paneData = amenitiesData.map((amenity) => ({
      id: amenity.uuid,
      nid: amenity.nid,
      title: amenity.title,
      subpaneButtonText: amenity.subpaneButtonText,
      coverImage: amenity.coverImage,
      category: amenity.category,
    }));

    return {
      id: 'amenities',
      btnText: 'My Amenities',
      paneData,
      subpaneComponent: memoizedPlaceDetail,
      stickyMarkup: stickyAmenitiesBlurb,
    };
  }, [amenitiesData, memoizedPlaceDetail, stickyAmenitiesBlurb]);

  const mediaPaneData = useMemo(() => {
    if (!mediaData || mediaData.length === 0) return null;

    // Transform data to format expected by SubpaneMediaDetail
    const paneData = mediaData.map((media) => ({
      id: media.uuid,
      nid: media.nid,
      title: media.title,
      subpaneButtonText: media.subpaneButtonText,
      coverImage: media.coverImage,
      type: media.type,
      // SubpaneMediaDetail expects video.videoId and video.service
      video: media.video ? {
        videoId: media.video.serviceVideoId,
        service: media.video.service,
      } : null,
      threeSixties: media.threeSixties,
      // SubpaneMediaDetail expects photoGalleryImages, not images
      photoGalleryImages: media.images,
    }));

    return {
      id: 'media',
      btnText: 'My Media',
      paneData,
      subpaneComponent: memoizedSubpaneMediaDetail,
      stickyMarkup: null,
    };
  }, [mediaData, memoizedSubpaneMediaDetail]);

  const resourcesPaneData = useMemo(() => {
    // A standalone pane only when there is no About pane to append it to.
    if (!hasResources || hasAbout) return null;

    return {
      id: 'resources',
      btnText: 'My Resources',
      paneData: createResourcesPaneData(resourcesData),
      subpaneComponent: memoizedResourceDetail,
      stickyMarkup: null,
    };
  }, [hasResources, hasAbout, resourcesData, memoizedResourceDetail]);

  // Combine all pane data into final array
  const panesDataMemo = useMemo(() => {
    // Wait for favorites data before creating panes
    if (!favoritesData) return null;

    const rawPanesData = [];

    // Add panes based on available data.
    if (residencesPaneData) rawPanesData.push(residencesPaneData);
    if (floorPlansPaneData) rawPanesData.push(floorPlansPaneData);
    if (amenitiesPaneData) rawPanesData.push(amenitiesPaneData);
    if (mediaPaneData) rawPanesData.push(mediaPaneData);
    // Add resources pane last
    if (resourcesPaneData) rawPanesData.push(resourcesPaneData);

    // Map is always available; add it first, then let the About pane lead.
    rawPanesData.unshift(mapPaneData);
    if (aboutPaneData) rawPanesData.unshift(aboutPaneData);

    return rawPanesData;
  }, [mapPaneData,
    aboutPaneData,
    residencesPaneData,
    floorPlansPaneData,
    amenitiesPaneData,
    mediaPaneData,
    resourcesPaneData,
    favoritesData]);

  // Update panesData when memoized data changes
  useEffect(() => {
    setPanesData(panesDataMemo);
  }, [panesDataMemo]);

  return (
    <div className="pibPage-favorites">
      <PanesWidget panesData={panesData} />
    </div>
  );
};

BrochurePageContent.propTypes = {
  communitySlug: PropTypes.string,
  mapFavoritesData: PropTypes.shape({
    residence: PropTypes.arrayOf(PropTypes.string),
    place: PropTypes.arrayOf(PropTypes.string),
  }),
  resourcesData: PropTypes.shape({
    links: PropTypes.array,
    files: PropTypes.array,
  }),
  amenitiesServicesHTML: PropTypes.string,
  aboutTitle: PropTypes.string,
  // TourGuide AI brochure passes the community's field_tgaib_fp_page_title;
  // PIB passes nothing and keeps the default.
  floorPlansTitle: PropTypes.string,
  buckets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    sections: PropTypes.array,
  })),
  initialFavoriteData: PropTypes.object,
  favoritesData: PropTypes.shape({
    residences: PropTypes.array,
    floorPlans: PropTypes.array,
    amenities: PropTypes.array,
    media: PropTypes.array,
  }),
  tracking: PropTypes.shape({
    endpoint: PropTypes.string,
    token: PropTypes.string,
    // PIB: { residentNid }; TourGuide AI lead: { contactHash, communityNid }.
    identity: PropTypes.shape({
      residentNid: PropTypes.number,
      contactHash: PropTypes.string,
      communityNid: PropTypes.number,
    }),
  }),
};

BrochurePageContent.defaultProps = {
  communitySlug: null,
  mapFavoritesData: null,
  resourcesData: null,
  amenitiesServicesHTML: null,
  aboutTitle: 'About',
  floorPlansTitle: 'My Floor Plans',
  buckets: null,
  initialFavoriteData: null,
  favoritesData: null,
  tracking: null,
};

export default BrochurePageContent;
